import { useEffect, useMemo, useState } from 'react';
import heroSlide1 from './assets/news/news3.jpg';
import heroSlide2 from './assets/news/news6.jpg';
import heroSlide3 from './assets/news/news8.jpg';
import eventImage1 from './assets/news/news1.jpg';
import eventImage2 from './assets/news/news4.jpg';
import eventImage3 from './assets/news/news5.jpg';
import eventImage4 from './assets/news/news7.jpg';
import eventImage5 from './assets/news/new2.jpg';

function EventsCompetitionsPage({ page }) {
  const heroSlides = page?.hero_slides || [heroSlide1, heroSlide2, heroSlide3];
  const [activeHero, setActiveHero] = useState(0);

  useEffect(() => {
    const intervalId = setInterval(() => {
      setActiveHero((current) => (current + 1) % heroSlides.length);
    }, 6000);

    return () => clearInterval(intervalId);
  }, [heroSlides.length]);

  const events = useMemo(
    () =>
      page?.events || [
        {
          title: 'SPARTAN CUPS',
          subtitle: 'Annual sports, arts, and leadership competitions',
          description:
            'Spartan Cups brings together students across levels for a full week of friendly competition, teamwork, and school pride. The program includes house teams, skill clinics, and a final celebration night with awards.',
          highlights: ['House leagues and leadership roles', 'Skill clinics led by coaches', 'Championship night and awards'],
          schedule: [
            'Week 1: Team drafting and leadership workshop',
            'Week 2: Qualifiers and sports clinics',
            'Week 3: Creative showcase + finals',
          ],
          images: [eventImage1, eventImage4, eventImage3],
        },
        {
          title: 'Entrepreneurship Program',
          subtitle: 'Ideas to impact: student-led enterprise journey',
          description:
            'Students learn how to build a business idea, pitch with confidence, and manage a mini enterprise. The program blends mentorship, budgeting, and storytelling so students gain real-world financial literacy and teamwork skills.',
          highlights: ['Mentor-led ideation labs', 'Market day with student booths', 'Pitching and storytelling training'],
          schedule: [
            'Phase 1: Idea sprint + customer interviews',
            'Phase 2: Budgeting and product testing',
            'Phase 3: Market day + final pitch',
          ],
          images: [eventImage2, eventImage5, eventImage3],
        },
        {
          title: 'STEM Program',
          subtitle: 'Innovation, coding, and scientific discovery',
          description:
            'From coding challenges to science fairs, the STEM program encourages experimentation and collaboration. Students work on real problems, build prototypes, and present solutions at the annual Innovation Expo.',
          highlights: ['Robotics and coding labs', 'Science fair exhibition', 'Innovation Expo and demos'],
          schedule: [
            'Cycle 1: Design thinking and research',
            'Cycle 2: Build, test, and iterate',
            'Cycle 3: Expo prep and showcases',
          ],
          images: [eventImage3, eventImage1, eventImage2],
        },
      ],
    [page]
  );

  const [activeEventSlides, setActiveEventSlides] = useState(() => events.map(() => 0));

  useEffect(() => {
    setActiveEventSlides(events.map(() => 0));
  }, [events]);

  useEffect(() => {
    const intervalId = setInterval(() => {
      setActiveEventSlides((current) =>
        current.map((value, index) => (value + 1) % events[index].images.length)
      );
    }, 4800);

    return () => clearInterval(intervalId);
  }, [events]);

  useEffect(() => {
    const animatedItems = document.querySelectorAll('.event-animate');
    if (!animatedItems.length) {
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.2 }
    );

    animatedItems.forEach((item) => observer.observe(item));

    return () => observer.disconnect();
  }, []);

  const heroTitle = page?.title || 'Events & Competitions';
  const heroSummary =
    page?.summary ||
    'Signature events that build confidence, creativity, and collaboration all year long.';

  return (
    <section className="events-page">
      <section className="events-hero" id="events-overview">
        <div className="events-hero-media" aria-hidden="true">
          {heroSlides.map((slide, index) => (
            <div
              key={slide}
              className={`events-hero-slide ${index === activeHero ? 'active' : ''}`}
            >
              <img src={slide} alt="Events hero" />
            </div>
          ))}
        </div>
        <div className="events-hero-overlay" aria-hidden="true" />
        <div className="events-hero-content">
          <p className="events-hero-kicker">Events & Competitions</p>
          <h2>{heroTitle}</h2>
          <p>{heroSummary}</p>
          <div className="events-hero-dots" role="tablist" aria-label="Events hero slides">
            {heroSlides.map((slide, index) => (
              <button
                key={slide}
                type="button"
                className={`events-hero-dot ${index === activeHero ? 'active' : ''}`}
                onClick={() => setActiveHero(index)}
                aria-label={`Show slide ${index + 1}`}
              />
            ))}
          </div>
        </div>
      </section>

      <section className="events-list" id="signature-programs">
        <div className="events-list-header">
          <p className="events-list-kicker">Signature Programs</p>
          <h3>Competitions, entrepreneurship, and STEM experiences</h3>
          <p>
            Each program combines mentoring, student showcases, and hands-on learning. Explore the schedule, highlights,
            and visuals for every event.
          </p>
        </div>

        <div className="events-grid">
          {events.map((event, index) => (
            <article className="events-card event-animate" key={event.title}>
              <div className="events-card-content">
                <p className="events-card-label">{event.subtitle}</p>
                <h4>{event.title}</h4>
                <p>{event.description}</p>
                <div className="events-card-section">
                  <h5>Highlights</h5>
                  <div className="events-chip-row">
                    {event.highlights.map((item) => (
                      <span key={item}>{item}</span>
                    ))}
                  </div>
                </div>
                <div className="events-card-section">
                  <h5>Program Flow</h5>
                  <ul>
                    {event.schedule.map((item) => (
                      <li key={item}>{item}</li>
                    ))}
                  </ul>
                </div>
              </div>

              <div className="events-card-media" aria-hidden="true">
                {event.images.map((image, imageIndex) => (
                  <div
                    key={`${event.title}-${image}`}
                    className={`events-card-slide ${
                      imageIndex === activeEventSlides[index] ? 'active' : ''
                    }`}
                  >
                    <img src={image} alt={`${event.title} activity`} />
                  </div>
                ))}
                <div className="events-card-dots">
                  {event.images.map((image, imageIndex) => (
                    <button
                      key={`${event.title}-${image}-dot`}
                      type="button"
                      className={`events-card-dot ${
                        imageIndex === activeEventSlides[index] ? 'active' : ''
                      }`}
                      onClick={() =>
                        setActiveEventSlides((current) =>
                          current.map((value, idx) => (idx === index ? imageIndex : value))
                        )
                      }
                      aria-label={`Show ${event.title} slide ${imageIndex + 1}`}
                    />
                  ))}
                </div>
              </div>
            </article>
          ))}
        </div>
      </section>
    </section>
  );
}

export default EventsCompetitionsPage;
